import { useCallback, useState } from 'react';

/**
 * The playhead, sampled once when a menu opens and held until the next open.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE VALUE MUST NOT TRACK THE PLAYER. THAT IS THE WHOLE CONTRACT.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * It backs the "Copy link at 1:30" row, whose label NAMES the timestamp. A live read would
 * relabel the row under the user's cursor several times a second, and — worse — make the label
 * a promise the click can't keep: the video plays on behind the open menu, so by the time you
 * click, a live value is a different second than the one you read. One frozen number backs both
 * the label and the link, which is the only way they can agree.
 *
 * **Never cleared on close.** Clearing would drop the timestamped row mid fade-out, so the last
 * thing the user sees is a menu item vanishing from under the click they just made. A stale
 * value costs nothing: `freezePlayhead` overwrites it before the menu reopens, and while closed
 * nothing reads it.
 *
 * Extracted because three surfaces repeated it (`VideoMenuButton` and both `MediaActionBar`
 * wrappers), each with its own copy of the two rules above — the drift risk this branch spends
 * most of its effort removing everywhere else.
 *
 * Pure `useState`, so it is safe on any route. A host with no video player never calls it.
 */
export function useFrozenPlayhead(getCurrentTime?: () => number | undefined) {
  const [playheadTime, setPlayheadTime] = useState<number | undefined>(undefined);

  // Stable, so it can be passed to a menu's `onMenuOpen` without re-rendering it each time.
  const freezePlayhead = useCallback(() => {
    setPlayheadTime(getCurrentTime?.());
  }, [getCurrentTime]);

  return { playheadTime, freezePlayhead };
}
